// LOL Nurse behaviors: free-shipping bar, cart upsell, size guide, header unit panel.
// No framework, no build step. Relies on Shopify's native `shopify:cart:lines-update`
// standard event (see assets/standard-events.d.ts) and the documented /cart/add.js
// AJAX API with the `sections` param, so it stays correct if Horizon's internals change.

class LnFreeShippingBar extends HTMLElement {
  connectedCallback() {
    this.threshold = Number(this.dataset.threshold || 0);
    if (this.threshold <= 0) return;

    this._onCartUpdate = this._onCartUpdate.bind(this);
    document.addEventListener('shopify:cart:lines-update', this._onCartUpdate);

    const seed = Number(this.dataset.cartTotal || 0);
    this.render(seed);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:cart:lines-update', this._onCartUpdate);
  }

  async _onCartUpdate(event) {
    try {
      const result = await event.promise;
      const amount = result?.cart?.cost?.totalAmount?.amount;
      if (amount != null) this.render(Number(amount));
    } catch (_err) {
      // Cart update failed upstream; leave the bar showing its last known state.
    }
  }

  render(total) {
    const remaining = Math.max(this.threshold - total, 0);
    const met = remaining === 0;
    const fill = this.querySelector('.ln-shipping-bar__fill');
    const label = this.querySelector('.ln-shipping-bar__label');
    const wrap = this.querySelector('.ln-shipping-bar');

    if (fill) fill.style.width = `${Math.min((total / this.threshold) * 100, 100)}%`;
    if (wrap) wrap.classList.toggle('ln-shipping-bar--met', met);

    if (label) {
      label.textContent = met
        ? this.dataset.metText || "You've unlocked free shipping!"
        : (this.dataset.remainingTemplate || 'Add {{ amount }} more for free shipping').replace(
            '{{ amount }}',
            this._formatMoney(remaining)
          );
    }
  }

  _formatMoney(amount) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
        maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
      }).format(amount);
    } catch (_err) {
      return `$${amount.toFixed(2)}`;
    }
  }
}

customElements.define('ln-free-shipping-bar', LnFreeShippingBar);

// ---------------------------------------------------------------------------
// Shared add-to-cart: used by both the cart upsell buttons and the PDP add-to-
// cart form. Adds via the AJAX cart API, asks Shopify to re-render the cart
// drawer section in the same response, swaps it in, then opens the drawer.
// This works regardless of which reactive internals Horizon ships, since it
// only depends on the documented Cart AJAX API.
async function ln_submitCartAdd(formData, button) {
  const originalLabel = button?.textContent;
  if (button) {
    button.disabled = true;
    button.textContent = '...';
  }

  let ok = true;
  try {
    formData.append('sections', 'cart-drawer-section');
    formData.append('sections_url', window.location.pathname);

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error('Add to cart failed');
    const data = await response.json();

    const drawerHtml = data.sections && data.sections['cart-drawer-section'];
    if (drawerHtml) {
      const wrapper = document.getElementById('shopify-section-cart-drawer-section');
      if (wrapper) {
        const parsed = new DOMParser().parseFromString(drawerHtml, 'text/html');
        const fresh = parsed.getElementById('shopify-section-cart-drawer-section');
        wrapper.outerHTML = fresh ? fresh.outerHTML : drawerHtml;
      }
    }

    await ln_refreshCartBubble();
    document.getElementById('cart-drawer')?.open?.();
  } catch (_err) {
    ok = false;
    if (button) button.textContent = 'Try again';
  }

  if (button && ok) {
    button.textContent = originalLabel;
    button.disabled = false;
  } else if (button) {
    button.disabled = false;
    setTimeout(() => {
      button.textContent = originalLabel;
    }, 2000);
  }

  return ok;
}

// Updates the header cart bubble AND every free-shipping bar on the current
// page (e.g. the PDP's own instance, which lives outside the cart-drawer
// section that ln_submitCartAdd already swaps in fresh). This raw fetch flow
// never dispatches Shopify's `shopify:cart:lines-update` standard event —
// that's only fired by Shopify's own components — so anything outside the
// swapped drawer HTML needs this manual nudge to stay in sync.
async function ln_refreshCartBubble() {
  try {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const countEl = document.getElementById('cart-bubble-text')?.querySelector('.cart-bubble__text-count');
    const bubbleEl = document.getElementById('cart-bubble-text')?.closest('.cart-bubble');
    if (countEl) countEl.textContent = cart.item_count > 0 ? cart.item_count : '';
    if (bubbleEl) bubbleEl.classList.toggle('visually-hidden', cart.item_count === 0);

    const total = Number(cart.total_price) / 100;
    document.querySelectorAll('ln-free-shipping-bar').forEach((bar) => {
      if (typeof bar.render === 'function') bar.render(total);
    });
  } catch (_err) {
    // Non-critical: the header count will correct itself on next navigation.
  }
}

document.addEventListener('click', (event) => {
  const upsellButton = event.target.closest('.ln-upsell__add');
  if (!upsellButton || upsellButton.disabled) return;
  const variantId = upsellButton.dataset.variantId;
  if (!variantId) return;
  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', '1');
  ln_submitCartAdd(formData, upsellButton);
});

document.addEventListener('submit', (event) => {
  const form = event.target.closest('form[data-ln-product-form]');
  if (!form) return;
  event.preventDefault();
  const submitButton = form.querySelector('[data-ln-add-to-cart]');
  ln_submitCartAdd(new FormData(form), submitButton);
});

// ---------------------------------------------------------------------------
// PDP variant picker: swatches/pills per option, matched client-side against
// the product's variants (embedded as JSON) to keep the hidden variant id,
// price and add-to-cart availability in sync. Self-contained — no dependency
// on Horizon's variant-picker.js.
class LnVariantPicker extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-variants]');
    this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
    this.selected = Array.from(this.querySelectorAll('.ln-option-row')).map((row) => {
      const current = row.querySelector('[aria-current="true"]');
      return current ? current.dataset.value : null;
    });

    this.addEventListener('click', (event) => {
      const control = event.target.closest('[data-option-index]');
      if (!control) return;
      const index = Number(control.dataset.optionIndex);
      this.selected[index] = control.dataset.value;
      this.querySelectorAll(`[data-option-index="${index}"]`).forEach((el) => {
        el.setAttribute('aria-current', String(el === control));
      });
      const label = this.querySelector(`[data-option-value-index="${index}"]`);
      if (label) label.textContent = `— ${control.dataset.value.toUpperCase()}`;

      if (control.dataset.fullSrc) {
        const gallery = this.closest('.ln-product__layout')?.querySelector('.ln-product__gallery');
        const mainImage = gallery?.querySelector('.ln-product__main-media img');
        if (mainImage) {
          mainImage.src = control.dataset.fullSrc;
          mainImage.removeAttribute('srcset');
          mainImage.alt = control.dataset.fullAlt || mainImage.alt;
        }
        gallery?.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
          thumb.setAttribute('aria-current', String(thumb.dataset.fullSrc === control.dataset.fullSrc));
        });
      }

      this._syncVariant();
    });

    this._syncVariant();
  }

  _syncVariant() {
    const allSelected = this.selected.every((value) => value != null && value !== '');
    const match = allSelected
      ? this.variants.find((variant) => variant.options.every((value, i) => value === this.selected[i]))
      : null;
    const form = this.closest('form');
    const idInput = form?.querySelector('input[name="id"]');
    const addButton = form?.querySelector('[data-ln-add-to-cart]');
    const sizeHint = form?.querySelector('[data-ln-size-hint]');
    const priceEl = this.closest('.ln-product__info')?.querySelector('.ln-price');

    if (idInput) idInput.value = match ? match.id : '';
    if (sizeHint) {
      sizeHint.hidden = allSelected;
      if (!allSelected) sizeHint.textContent = this._missingOptionsMessage(sizeHint.dataset.template);
    }
    if (addButton) {
      if (!allSelected) {
        addButton.disabled = true;
        addButton.textContent = addButton.dataset.addLabel;
      } else {
        const available = Boolean(match && match.available);
        addButton.disabled = !available;
        addButton.textContent = available
          ? addButton.dataset.addLabel
          : match
          ? addButton.dataset.soldOutLabel
          : addButton.dataset.unavailableLabel;
      }
    }
    if (priceEl && match) this._renderPrice(priceEl, match);
    this._updateOptionAvailability();
  }

  // Names whichever option(s) still need a value — "color", "size", or
  // "color and size" — rather than a fixed "pick a size" message that would
  // be wrong if size is already set but color (or some other option) isn't.
  _missingOptionsMessage(template) {
    const names = Array.from(this.querySelectorAll('.ln-option-row'))
      .filter((row) => {
        const index = Number(row.dataset.optionIndex);
        const value = this.selected[index];
        return value == null || value === '';
      })
      .map((row) => (row.dataset.optionName || '').toLowerCase())
      .filter(Boolean);

    const list =
      names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0] || '';

    return (template || 'Pick a {{ options }} to add to cart.').replace('{{ options }}', list);
  }

  // Marks each option button as unavailable (crossed out) when no available
  // variant exists combining it with whatever's already selected on the other
  // option(s) — e.g. a size that's sold out in the currently-picked color.
  _updateOptionAvailability() {
    this.querySelectorAll('[data-option-index]').forEach((button) => {
      const index = Number(button.dataset.optionIndex);
      const hypothetical = this.selected.slice();
      hypothetical[index] = button.dataset.value;
      const possible = this.variants.some(
        (variant) =>
          variant.available &&
          variant.options.every((value, i) => hypothetical[i] == null || hypothetical[i] === '' || value === hypothetical[i])
      );
      button.classList.toggle('ln-option-pill--unavailable', !possible);
      button.classList.toggle('ln-swatch--unavailable', !possible);
    });
  }

  _renderPrice(el, variant) {
    const format = (cents) => {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
        }).format(cents / 100);
      } catch (_err) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    };

    el.innerHTML =
      variant.compare_at_price > variant.price
        ? `<span class="ln-price__sale">${format(variant.price)}</span><span class="ln-price__compare">${format(
            variant.compare_at_price
          )}</span>`
        : `<span>${format(variant.price)}</span>`;
  }
}

customElements.define('ln-variant-picker', LnVariantPicker);

// ---------------------------------------------------------------------------
// Size guide: a native <dialog>, opened/closed from any trigger/close button
// that points at it via data-size-guide-target.
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-size-guide-target]');
  if (trigger) {
    const dialog = document.getElementById(trigger.dataset.sizeGuideTarget);
    dialog?.showModal?.();
    return;
  }

  const closer = event.target.closest('[data-size-guide-close]');
  if (closer) {
    closer.closest('dialog')?.close();
  }
});

// ---------------------------------------------------------------------------
// Header unit panel + mobile nav: click-to-toggle on touch/small screens, CSS
// :hover covers pointer devices for the unit panel on desktop. Each header has
// two independent panels (unit panel, mobile nav); opening one closes the
// other so they never stack. Both close on outside click and Escape. The
// unit-toggle button is rendered twice (desktop nav + mobile nav), so its
// aria-expanded state is synced across every match, not just the first.
function ln_closeUnitPanel(header) {
  header.dataset.unitPanelOpen = 'false';
  header.querySelectorAll('.ln-header__unit-toggle').forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
}

function ln_closeMobileNav(header) {
  header.dataset.mobileNavOpen = 'false';
  header.querySelector('.ln-header__menu-toggle')?.setAttribute('aria-expanded', 'false');
}

document.addEventListener('click', (event) => {
  const unitToggle = event.target.closest('.ln-header__unit-toggle');
  const menuToggle = event.target.closest('.ln-header__menu-toggle');
  const header = event.target.closest('.ln-header');

  if (unitToggle) {
    const currentHeader = unitToggle.closest('.ln-header');
    const isOpen = currentHeader.dataset.unitPanelOpen === 'true';
    ln_closeMobileNav(currentHeader);
    currentHeader.dataset.unitPanelOpen = String(!isOpen);
    currentHeader.querySelectorAll('.ln-header__unit-toggle').forEach((btn) => btn.setAttribute('aria-expanded', String(!isOpen)));
    return;
  }

  if (menuToggle) {
    const currentHeader = menuToggle.closest('.ln-header');
    const isOpen = currentHeader.dataset.mobileNavOpen === 'true';
    ln_closeUnitPanel(currentHeader);
    currentHeader.dataset.mobileNavOpen = String(!isOpen);
    menuToggle.setAttribute('aria-expanded', String(!isOpen));
    return;
  }

  if (!header) {
    document.querySelectorAll('.ln-header[data-unit-panel-open="true"]').forEach(ln_closeUnitPanel);
    document.querySelectorAll('.ln-header[data-mobile-nav-open="true"]').forEach(ln_closeMobileNav);
  }
});

// Unit panel tabs: hovering, focusing, or clicking a group on the left shows its units on the right.
function ln_selectUnitTab(tab) {
  const rail = tab.closest('.ln-unit-rail');
  if (!rail || tab.getAttribute('aria-selected') === 'true') return;
  rail.querySelectorAll('[data-unit-tab]').forEach((t) => {
    const selected = t === tab;
    t.setAttribute('aria-selected', String(selected));
    const pane = rail.querySelector('#' + t.getAttribute('aria-controls'));
    if (pane) pane.hidden = !selected;
  });
}

document.addEventListener('mouseover', (event) => {
  const tab = event.target.closest('[data-unit-tab]');
  if (tab) ln_selectUnitTab(tab);
});

document.addEventListener('focusin', (event) => {
  const tab = event.target.closest('[data-unit-tab]');
  if (tab) ln_selectUnitTab(tab);
});

document.addEventListener('click', (event) => {
  const tab = event.target.closest('[data-unit-tab]');
  if (tab) ln_selectUnitTab(tab);
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('.ln-header[data-unit-panel-open="true"]').forEach(ln_closeUnitPanel);
  document.querySelectorAll('.ln-header[data-mobile-nav-open="true"]').forEach(ln_closeMobileNav);
});

// ---------------------------------------------------------------------------
// Collection filters: below the desktop breakpoint the sidebar is hidden by
// default (there's no room for a permanent 220px column), so this toggle
// expands/collapses it in place from the toolbar's "Filters" button.
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('.ln-collection__filter-toggle');
  if (!toggle) return;
  const layout = toggle.closest('.ln-collection__layout');
  if (!layout) return;
  const isOpen = layout.dataset.filtersOpen === 'true';
  layout.dataset.filtersOpen = String(!isOpen);
  toggle.setAttribute('aria-expanded', String(!isOpen));
});

// ---------------------------------------------------------------------------
// Collection sort-by dropdown: navigates with the sort_by param, keeping any
// existing filter query params intact.
document.addEventListener('change', (event) => {
  const sortSelect = event.target.closest('[data-ln-sort]');
  if (!sortSelect) return;
  const url = new URL(window.location.href);
  url.searchParams.set('sort_by', sortSelect.value);
  window.location.href = url.toString();
});

// ---------------------------------------------------------------------------
// Collection price filter: fixed tiers (Under $10 / $10-$20 / $20+) rather
// than a free min/max range. Sets/clears the param names Shopify's own
// collection.filters.min_value/max_value expose, so it composes correctly
// with any other active filters — an empty data-min/data-max means that end
// of the tier is open, so the existing param (if any) is cleared instead.
document.addEventListener('click', (event) => {
  const tier = event.target.closest('[data-ln-price-tier]');
  if (!tier) return;

  const url = new URL(window.location.href);

  if (tier.dataset.min) {
    url.searchParams.set(tier.dataset.paramMin, tier.dataset.min);
  } else {
    url.searchParams.delete(tier.dataset.paramMin);
  }

  if (tier.dataset.max) {
    url.searchParams.set(tier.dataset.paramMax, tier.dataset.max);
  } else {
    url.searchParams.delete(tier.dataset.paramMax);
  }

  window.location.href = url.toString();
});

// ---------------------------------------------------------------------------
// PDP gallery: clicking a thumbnail swaps the main image. Simpler than
// reconstructing a full responsive srcset per click — the first image still
// gets one on initial load for LCP.
document.addEventListener('click', (event) => {
  const thumb = event.target.closest('[data-gallery-thumb]');
  if (!thumb) return;

  const gallery = thumb.closest('.ln-product__gallery');
  const mainImage = gallery?.querySelector('.ln-product__main-media img');
  if (mainImage && thumb.dataset.fullSrc) {
    mainImage.src = thumb.dataset.fullSrc;
    mainImage.removeAttribute('srcset');
    mainImage.alt = thumb.dataset.fullAlt || '';
  }

  gallery?.querySelectorAll('[data-gallery-thumb]').forEach((el) => {
    el.setAttribute('aria-current', String(el === thumb));
  });
});

// ---------------------------------------------------------------------------
// PDP quantity stepper.
document.addEventListener('click', (event) => {
  const decreaseButton = event.target.closest('[data-ln-qty-decrease]');
  const increaseButton = event.target.closest('[data-ln-qty-increase]');
  if (!decreaseButton && !increaseButton) return;

  const stepper = (decreaseButton || increaseButton).closest('.ln-qty-stepper');
  const input = stepper?.querySelector('input[type="number"]');
  if (!input) return;

  const min = Number(input.min || 1);
  const current = Number(input.value || 1);
  input.value = String(decreaseButton ? Math.max(min, current - 1) : current + 1);
});
